// Integrates locally bundled htmx responses with the application's existing
// error reporting while allowing expected validation fragments to render.
import { requestConfirmation } from './confirmation'
import { throwFrontendError } from './errors'
import { Log } from './logger'

type BeforeSwapDetail = {
  xhr: XMLHttpRequest
  shouldSwap: boolean
  isError: boolean
}

type HtmxEventDetail = {
  xhr?: XMLHttpRequest
}

type HtmxConfirmDetail = {
  question?: string | null
  issueRequest: (skipConfirmation?: boolean) => void
}

/**
 * Replaces htmx's native confirmation prompt with the shared application dialog.
 */
async function handleConfirmation(event: Event): Promise<void> {
  await Log.info('Handle asynchronous request confirmation', async () => {
    /**
     * Carries the question and continuation provided by htmx for this request.
     */
    const detail = (event as CustomEvent<HtmxConfirmDetail>).detail

    if (!detail?.question) {
      return
    }

    // Cancellation must happen before awaiting the user's choice or htmx will
    // continue immediately into its own browser-native confirmation.
    event.preventDefault()

    if (await requestConfirmation(detail.question)) {
      detail.issueRequest(true)
    }
  })
}

/**
 * Lets an expected validation response update only its server-selected status target.
 */
function handleBeforeSwap(event: Event): void {
  /**
   * Carries the response state that htmx permits this handler to adjust.
   */
  const detail = (event as CustomEvent<BeforeSwapDetail>).detail
  const status = detail?.xhr.status

  Log.info(`Handle asynchronous HTTP ${status ?? 'unknown'} response swap`, () => {
    if (detail?.xhr.status !== 422) {
      return
    }

    detail.shouldSwap = true
    detail.isError = false
  })
}

/**
 * Reports failed asynchronous actions without replacing the current working interface.
 */
function handleRequestFailure(event: Event): void {
  /**
   * Carries the failed request without assuming every htmx event has an XHR.
   */
  const detail = (event as CustomEvent<HtmxEventDetail>).detail
  const status = detail?.xhr?.status
  const description = status === undefined || status === 0
    ? 'The asynchronous request could not reach the server.'
    : `The asynchronous request failed with HTTP ${status}.`

  // Throw outside htmx's own event stack so diagnostics remain observable
  // without interrupting its cleanup of disabled controls and indicators.
  window.setTimeout(() => {
    throwFrontendError(new Error(description))
  }, 0)
}

/**
 * Records the authoritative response status after an asynchronous action completes.
 */
function handleRequestCompleted(event: Event): void {
  const customEvent = event as CustomEvent<HtmxEventDetail>
  const detail = customEvent.detail
  const status = detail?.xhr?.status

  Log.notice(`Complete asynchronous page request with HTTP ${status ?? 'unknown'}`, () => {
    if (detail?.xhr === undefined) {
      throw new Error('An asynchronous response did not include its request.')
    }
  })
}

document.body.addEventListener('htmx:confirm', (event) => {
  void handleConfirmation(event)
})

document.body.addEventListener('htmx:beforeSwap', handleBeforeSwap)
document.body.addEventListener('htmx:afterRequest', handleRequestCompleted)
document.body.addEventListener('htmx:responseError', handleRequestFailure)
document.body.addEventListener('htmx:sendError', handleRequestFailure)
